import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { articleList } from '@/mocks/articles';

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [catOpen, setCatOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) setGuideOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileCatOpen(false);
    setMobileGuideOpen(false);
  };

  const textClass = scrolled
    ? 'text-foreground-700 hover:text-foreground-950'
    : 'text-white/90 hover:text-white';
  const logoClass = scrolled ? 'text-foreground-950' : 'text-white';
  const borderClass = scrolled ? 'border-background-200/70' : 'border-white/10';
  const bgClass = scrolled ? 'bg-background-50 shadow-sm' : 'bg-transparent';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <button
            onClick={() => { navigate('/'); closeMobile(); }}
            className={`text-lg md:text-xl font-heading font-bold whitespace-nowrap cursor-pointer ${logoClass}`}
          >
            cssbuy<span className={scrolled ? 'text-accent-500' : 'text-accent-400'}>.</span>pro
          </button>

          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={() => navigate('/')}
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${textClass}`}
            >
              {t('nav_home')}
            </button>

            <div ref={catRef} className="relative">
              <button
                onClick={() => { setCatOpen(!catOpen); setGuideOpen(false); }}
                className={`flex items-center gap-1.5 text-sm transition-colors whitespace-nowrap cursor-pointer ${textClass}`}
              >
                {t('nav_categories')}
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${catOpen ? 'rotate-180' : ''}`}></i>
                </span>
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 mt-2 w-60 bg-background-50 rounded-xl border border-background-200/70 py-2 shadow-lg z-50 max-h-[70vh] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.href}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      onClick={() => setCatOpen(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-accent-500`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div ref={guideRef} className="relative">
              <button
                onClick={() => { setGuideOpen(!guideOpen); setCatOpen(false); }}
                className={`flex items-center gap-1.5 text-sm transition-colors whitespace-nowrap cursor-pointer ${textClass}`}
              >
                Guia de Compras
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}></i>
                </span>
              </button>
              {guideOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 py-2 shadow-lg z-50 max-h-[70vh] overflow-y-auto">
                  <p className="px-4 py-2 text-xs text-foreground-400 uppercase tracking-wider font-semibold">Guias SEO</p>
                  {articleList.map((article) => (
                    <button
                      key={article.slug}
                      onClick={() => { navigate(`/artigo/${article.slug}`); setGuideOpen(false); }}
                      className="w-full text-left flex items-start gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer border-b border-background-100 last:border-b-0"
                    >
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-article-line text-accent-500"></i>
                      </span>
                      <span className="leading-snug">{article.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`hidden sm:inline-flex text-sm transition-colors whitespace-nowrap cursor-pointer ${textClass}`}
            >
              CSSBuy Sheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap cursor-pointer transition-all ${
                scrolled
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shopping-bag-line"></i>
              </span>
              Buy It Now
            </a>

            <button
              className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${textClass}`}
              onClick={() => { setMobileOpen(!mobileOpen); setCatOpen(false); setGuideOpen(false); }}
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-0.5 mb-4 pb-3 border-b border-background-100">
            <button
              onClick={() => { navigate('/'); closeMobile(); }}
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              <span className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-home-4-line text-accent-600 text-sm"></i>
              </span>
              {t('nav_home')}
            </button>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
              onClick={closeMobile}
            >
              <span className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-shopping-bag-line text-primary-600 text-sm"></i>
              </span>
              CSSBuy Sheet
            </a>
          </div>

          <button
            onClick={() => setMobileCatOpen(!mobileCatOpen)}
            className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-grid-line text-accent-600 text-sm"></i>
              </span>
              {t('nav_categories')}
            </div>
            <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}>
              <i className="ri-arrow-down-s-line text-foreground-500"></i>
            </span>
          </button>
          {mobileCatOpen && (
            <div className="ml-3 mt-1 mb-2 grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.href}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 text-xs text-foreground-700 hover:bg-background-100 hover:text-foreground-950 rounded-lg transition-colors"
                  onClick={closeMobile}
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${cat.icon} text-accent-500`}></i>
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>
          )}

          <button
            onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
            className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-book-open-line text-secondary-600 text-sm"></i>
              </span>
              Guia de Compras
            </div>
            <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileGuideOpen ? 'rotate-180' : ''}`}>
              <i className="ri-arrow-down-s-line text-foreground-500"></i>
            </span>
          </button>
          {mobileGuideOpen && (
            <div className="ml-3 mt-1 mb-2 flex flex-col gap-0.5">
              {articleList.map((article) => (
                <button
                  key={article.slug}
                  onClick={() => { navigate(`/artigo/${article.slug}`); closeMobile(); }}
                  className="flex items-center gap-2 px-3 py-2.5 text-xs text-foreground-700 hover:bg-background-100 hover:text-foreground-950 rounded-lg transition-colors text-left cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-article-line text-accent-500"></i>
                  </span>
                  <span className="leading-snug">{article.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}